import {
  ShiftManagementAreasViewPageDocument,
  ShiftManagementMainPageDocument,
  ShiftManagementScheduleApplyTemplateDocument,
  ShiftManagementScheduleApplyTemplateFetchDataDocument,
} from 'generated/resources/graphql'
import isEmpty from 'lodash/isEmpty'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { ConflictsProps } from 'modules/shift_management/lib//types'
import Api from 'modules/shift_management/lib/Api'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'

export interface Props {
  params: {
    selectedTemplateId: number
    selectedWeek?: number
    startDate: moment.Moment
    endDate: moment.Moment
    employeeIds: number[]
    notes: string
    locationId: number
    workAreaId: number
  }
  from: 'side_panel' | 'modal'
  onSuccess: (noConflicts: boolean, conflicts: ConflictsProps[]) => void
}

const useAssignTemplate = ({ params, from, onSuccess }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()

  const startDateParam = dateToString(params.startDate, 'backend')
  const endDateParam = dateToString(params.endDate, 'backend')
  const { refreshStats } = useRefreshStats()
  const selectedTemplateId = params.selectedTemplateId
  const employeeIds = params.employeeIds
  const weekPosition = params.selectedWeek || 0
  const notes = params.notes
  const locationId = params.locationId
  const workAreaId = params.workAreaId

  const [applyTemplate] = useMutation(ShiftManagementScheduleApplyTemplateDocument, {
    extractorFn: (data) => data.shiftManagementMutations.applyTemplateShiftmanagementSchedule,
  })

  const [executeFetchQuery] = useLazyQuery(ShiftManagementScheduleApplyTemplateFetchDataDocument)

  const { running: submitting, performAction } = useAsyncAction(
    async () => {
      const result = await asyncAction(
        () =>
          Api.checkOverlappingShifts(
            selectedTemplateId,
            weekPosition,
            employeeIds,
            startDateParam,
            endDateParam
          ),
        {
          successFlash: false,
        }
      )

      if (isSuccessful(result)) {
        const { value: conflicts } = result
        const noConflicts = isEmpty(conflicts)

        if (noConflicts) {
          const mutationResult = await applyTemplate({
            variables: {
              viewStartDate: view.startAt,
              viewEndDate: view.endAt,
              templateId: selectedTemplateId,
              templateWeekPosition: Number(weekPosition),
              employeesIds: employeeIds,
              startDate: startDateParam,
              endDate: endDateParam,
              notes,
              locationId,
              workAreaId,
            },
            // TODO: Actually update the cache properly
            refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
          })

          const mutationHandled = handleResult(mutationResult, (_errors) => {})

          if (mutationHandled.success) {
            const { shiftIds, restDayIds } = mutationHandled.data?.schedule || {}

            const { data: fetchData } = await executeFetchQuery({
              query: ShiftManagementScheduleApplyTemplateFetchDataDocument,
              variables: {
                shiftIds: shiftIds || [],
                restDayIds: restDayIds || [],
              },
            })

            const { shifts: newShifts, restDays: newRestDays } = fetchData?.shiftManagement || {}
            const shifts = (newShifts || []).map((shift) =>
              Shift.buildFromGraphQL({
                shift,
                employeeFullName: shift?.employee?.fullName || '',
                employeeAvatar: shift?.employee?.avatar?.url || '',
                employeeLocationId: shift?.employee?.locationId || 0,
              })
            )
            const restDays = (newRestDays || []).map((restDay) =>
              RestDay.build({
                id: restDay.id,
                employee_id: restDay.employee?.id || 0,
                date: restDay.date,
                state: restDay.state,
                timezone: restDay.timezone || '',
                employeeFullName: restDay.employee?.fullName || '',
                employeeAvatar: restDay.employee?.avatar?.url || '',
              })
            )

            const shiftColors = shifts.map((shift) => shift.color)
            trackAddShiftSubmitEvent({
              view,
              employeeIds,
              shiftsCount: shifts.length,
              restDaysCount: restDays.length,
              shiftType: 'template',
              color: shiftColors,
              from,
              withSearch: '',
              advancedOptions: false,
              extras: {
                template_id: selectedTemplateId,
                starting_template_week: weekPosition + 1, // for readability so first week is 1
              },
            })
            app.setFlash({
              type: 'correct',
              message: i18n.t('shift_management.actions.apply_rotating_shift_success'),
            })

            dispatchSchedule({ type: 'ADD_SHIFTS', shifts })
            dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts, view })
            dispatchSchedule({ type: 'ADD_REST_DAYS', restDays })
            dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays })
            dispatchUI({ type: 'REFRESHING_STATS' })

            onSuccess(noConflicts, conflicts)

            await refreshStats(shiftsRows)
          }
        } else {
          onSuccess(noConflicts, conflicts)
        }
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  return {
    onSubmit: performAction,
    submitting,
  }
}

export default useAssignTemplate
